import html2canvas from 'html2canvas'
import { computed, ref } from 'vue'
import { REQUEST_STATUS_LIST } from '@/utils/request-status'
import type { UseScreenCaptureReturn } from '@/composables/useScreenCapture/types'

export const useScreenCapture = (initialFileName: string = 'captura.png'): UseScreenCaptureReturn => {
  const elementToCapture = ref<HTMLElement | null>(null)
  const downloadFileName = ref<string>(initialFileName)
  const requestStatus = ref<REQUEST_STATUS_LIST>(REQUEST_STATUS_LIST.IDLE)
  const requestError = ref<Error | null>(null)

  const isLoading = computed((): boolean => {
    return requestStatus.value === REQUEST_STATUS_LIST.LOADING
  })

  const setElement = (el: HTMLElement) => {
    elementToCapture.value = el
  }

  const setFileName = (fileName: string) => {
    downloadFileName.value = fileName
  }

  const captureAndDownload = async () => {
    if (!elementToCapture.value) {
      throw new Error('The element to capture has not been assigned.')
    }

    requestStatus.value = REQUEST_STATUS_LIST.LOADING
    try {
      const canvas = await html2canvas(elementToCapture.value, { useCORS: true })
      const dataURL = canvas.toDataURL('image/png')
      const byteString = atob(dataURL.split(',')[1])
      const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0]
      const ab = new ArrayBuffer(byteString.length)
      const ia = new Uint8Array(ab)
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i)
      }
      const blob = new Blob([ab], { type: mimeString })
      const file = new File([blob], downloadFileName.value, { type: mimeString })

      const link = document.createElement('a')
      link.href = URL.createObjectURL(file)
      link.download = file.name
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      requestStatus.value = REQUEST_STATUS_LIST.SUCCESS
    } catch (error) {
      requestStatus.value = REQUEST_STATUS_LIST.ERROR
      requestError.value = error
    }
  }

  return {
    elementToCapture,
    downloadFileName,
    setElement,
    setFileName,
    captureAndDownload,
    isLoading,
    requestError,
    requestStatus
  }
}
